import React, { useState, useEffect, useMemo, useRef } from "react";
import { BsDot } from "react-icons/bs";
import dashboard from "../../../assets/stat/dashboard.jpg";
import datasources from "../../../assets/stat/datasources.jpg";
import playground from "../../../assets/stat/playground.jpg";
import leaddata from "../../../assets/stat/leaddata.jpg";
import usges from "../../../assets/stat/usges.jpg";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { TbLayoutDashboardFilled, TbCubePlus } from "react-icons/tb";
import { HiOutlineSparkles } from "react-icons/hi2";
import { BiSolidBarChartAlt2 } from "react-icons/bi";
import { SiGoogleadsense } from "react-icons/si";
import { TextGenerateEffect } from "./index";

// FeatureCard component
const FeatureCard = ({ item, isActive, onClick, markAsViewed }) => (
  <div
    className={`cursor-pointer p-4 rounded-xl shadow-md border transition-all duration-300
      ${isActive ? "bg-primary text-white" : "bg-white text-gray-900"}`}
    onClick={() => {
      onClick(item.key);
      markAsViewed(item.key);
    }}>
    <h3 className="text-lg font-semibold mb-1 flex gap-1 items-center">
      {item.icon}
      {item.name}
    </h3>
    <p className="text-sm">{item.description}</p>
  </div>
);

const Prodemo = () => {
  const [currIndex, setCurrIndex] = useState(0);
  const [viewedItems, setViewedItems] = useState({});

  const sos = useMemo(
    () => [
      {
        key: 0,
        src: dashboard,
        name: "Dashboard",
        description:
          "Monitor and manage your chatbot’s performance with real-time insights.",
        icon: <TbLayoutDashboardFilled size={20} />,
      },
      {
        key: 1,
        src: playground,
        name: "Playground",
        description:
          "Experiment and fine-tune chatbot responses in a controlled environment.",
        icon: <HiOutlineSparkles size={20} />,
      },
      {
        key: 2,
        src: datasources,
        name: "Sources",
        description:
          "Connect and manage data sources for more accurate chatbot responses.",
        icon: <TbCubePlus size={20} />,
      },
      {
        key: 3,
        src: usges,
        name: "Analytics",
        description:
          "Gain insights into chatbot interactions and user engagement.",
        icon: <BiSolidBarChartAlt2 size={20} />,
      },
      {
        key: 4,
        src: leaddata,
        name: "Lead Generation",
        description:
          "Capture and qualify leads automatically through AI-powered interactions.",
        icon: <SiGoogleadsense size={20} />,
      },
    ],
    []
  );

  const markAsViewed = (key) => {
    setViewedItems((prev) => ({ ...prev, [key]: true }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrIndex((prevIndex) => (prevIndex + 1) % sos.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [sos.length]);

  const ref = useRef(null);

  const inView = useInView(ref, {
    triggerOnce: true,
    rootMargin: "-100px 0px",
  });

  const cardRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  // Auto-scroll to active card on index change
  // useEffect(() => {
  //   if (cardRefs.current[currIndex]) {
  //     cardRefs.current[currIndex].scrollIntoView({
  //       behavior: "smooth",
  //       inline: "center",
  //       block: "nearest",
  //     });
  //   }
  // }, [currIndex]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const targetCard = cardRefs.current[currIndex];

    if (container && targetCard) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = targetCard.getBoundingClientRect();

      const scrollLeft =
        container.scrollLeft +
        (cardRect.left - containerRect.left) -
        container.clientWidth / 2 +
        cardRect.width / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [currIndex]);

  return (
    <div ref={ref} className="px-4 py-12 max-w-screen-xl  ">
      <div className="border border-1 text-[#b626f1] border-lightprimary p-1 px-3 rounded-xl flex justify-center items-center w-fit">
        <BsDot className="w-5 h-5 scale-150 fill-primary" />
        Explore
      </div>
      <div className="mt-3 text-2xl sm:text-5xl font-semibold text-primary ">
        <TextGenerateEffect
          words="Discover Jaano AI"
          start={inView}
          textcolor="white"
        />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-6">
        {/*Feature Cards - Desktop */}
        <div className="hidden sm:flex w-2/3 flex-col gap-4">
          {sos.map((item) => (
            <FeatureCard
              key={item.key}
              item={item}
              isActive={currIndex === item.key}
              onClick={(key) => setCurrIndex(key)}
              markAsViewed={markAsViewed}
            />
          ))}
        </div>

        {/* Feature Cards - Mobile/Tablet */}
        <div
          ref={scrollContainerRef}
          className="sm:hidden w-full overflow-x-auto overflow-y-hidden snap-x snap-mandatory px-2 pb-4 scroll-smooth">
          <div className="flex gap-4 w-max">
            {sos.map((item, index) => (
              <div
                key={item.key}
                ref={(el) => (cardRefs.current[index] = el)}
                className="min-w-[85vw] bg-black  p-4 flex flex-col gap-4  max-w-[85vw] snap-center  rounded-2xl overflow-hidden shadow-md flex-shrink-0 border border-gray-200">
                <img
                  src={item.src}
                  alt={item.name}
                  className="w-full aspect-video object-cover rounded-t-2xl"
                />

                <FeatureCard
                  item={item}
                  isActive={currIndex === item.key}
                  onClick={(key) => setCurrIndex(key)}
                  markAsViewed={markAsViewed}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Feature Image - Desktop */}
        <div className="hidden md:flex w-full p-auto  rounded-xl items-center justify-center sticky top-20 h-fit">
          <AnimatePresence mode="wait">
            <motion.img
              key={sos[currIndex].key}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              src={sos[currIndex].src}
              alt={sos[currIndex].name}
              className="rounded-lg shadow-lg object-cover aspect-video w-full h-auto"
            />
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Prodemo;
